import { useEffect, useMemo, useState } from "react";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";
import { Bar } from "react-chartjs-2";
import { parseChartRows, formatChartNumber } from "../../utils/chartRows";
import { CHART_USER_COLOR, CHART_COMPARISON_COLORS, isMobileViewport } from "../../utils/chartTheme";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const OTHER_COLOR = CHART_COMPARISON_COLORS[0];
// Keeps 20+ categories readable inside the horizontal-scroll wrapper instead
// of squeezing them into the visible width.
const MIN_CATEGORY_SLOT_WIDTH = 36;

// Live chart rows are always `{ label, value, is_user }` — no `columns` array.
const BarChart = ({ item, yAxisTitle }) => {
  const [yTitleFontSize, setYTitleFontSize] = useState(isMobileViewport() ? 12 : 16);

  useEffect(() => {
    const handleResize = () => setYTitleFontSize(isMobileViewport() ? 12 : 16);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const points = useMemo(
    () =>
      parseChartRows(item)
        // Smallest to largest left-to-right, regardless of payload order; the
        // user's bar lands wherever their value ranks, not at a fixed spot.
        .sort((a, b) => a.value - b.value),
    [item],
  );

  if (points.length === 0) return null;

  const labels = points.map(point => point.label);
  const values = points.map(point => point.value);
  const backgroundColors = points.map(point => (point.isUser ? CHART_USER_COLOR : OTHER_COLOR));

  // Live payload uses `user_answer_label` / `system_data_label`; keep the
  // shorter `user` / `other` keys too in case another source sends those.
  const legendUserLabel = item?.legend?.user_answer_label || item?.legend?.user || "Your Data";
  const legendOtherLabel = item?.legend?.system_data_label || item?.legend?.other || "Our Data";

  const data = {
    labels,
    datasets: [
      {
        // Single dataset holds both user and system bars, so it has no
        // meaningful global label - the tooltip callback below picks the
        // right legend text per bar via `points[dataIndex].isUser`.
        label: "",
        data: values,
        backgroundColor: backgroundColors,
        borderRadius: {
          topLeft: 6,
          topRight: 6,
        },
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: {
        callbacks: {
          label: context => {
            const seriesLabel = points[context.dataIndex]?.isUser ? legendUserLabel : legendOtherLabel;
            return `${seriesLabel}: ${formatChartNumber(context.raw)}`;
          },
        },
      },
    },
    scales: {
      y: {
        // No `max`/`stepSize` override: Chart.js's own linear-scale algorithm
        // rounds the top to a clean number and lays out ~8-11 readable ticks.
        beginAtZero: true,
        ticks: {
          callback: value => formatChartNumber(value),
        },
        grid: {
          drawBorder: false,
          color: "#F2F4F7",
        },
        title: {
          display: true,
          text: yAxisTitle || "Value",
          color: "#475467",
          font: {
            size: yTitleFontSize,
            weight: "600",
          },
        },
      },
      x: {
        grid: { display: false },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          autoSkip: false,
        },
        title: { display: false },
      },
    },
  };

  const scrollMinWidth = points.length * MIN_CATEGORY_SLOT_WIDTH;

  return (
    <div className="report-bar-chart mb-64">
      <div className="chart-legend flex flex-align-center flex-justify-end flex-wrap mb-12">
        <p className="chart-legend__item medium-text text-color-grey mr-20">
          <span className="chart-legend__dot" style={{ background: CHART_USER_COLOR }} />
          {legendUserLabel}
        </p>
        <p className="chart-legend__item medium-text text-color-grey">
          <span className="chart-legend__dot" style={{ background: OTHER_COLOR }} />
          {legendOtherLabel}
        </p>
      </div>
      <div className="report-bar-chart__scroll">
        <div className="report-bar-chart__canvas" style={{ minWidth: `${scrollMinWidth}px` }}>
          <Bar data={data} options={options} />
        </div>
      </div>
      {item?.x_axis_label && <div className="horizontal-value text-center text-color-grey">{item.x_axis_label}</div>}
    </div>
  );
};

export default BarChart;
